import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import HomePage from './pages/HomePage';
import SportsPage from './pages/SportsPage';
import LivePage from './pages/LivePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import WalletPage from './pages/WalletPage';
import DashboardPage from './pages/DashboardPage';
import MyBetsPage from './pages/MyBetsPage';
import UserAccountPage from './pages/UserAccountPage';
import PromotionsPage from './pages/PromotionsPage';
import ChampionshipPage from './pages/ChampionshipPage';
import BetSlip from './components/BetSlip';
import MobileBetBar from './components/MobileBetBar';
import { useAuth } from './auth/useAuth';
import { useNotificationPolling } from './auth/useNotificationPolling';
import './App.css';

function AppInner() {
  const { user, balance, logout, placeBet, fetchWallet, isLoading, accessToken } = useAuth();
  const navigate = useNavigate();

  const [bets, setBets]                     = useState([]);
  const [theme, setTheme]                   = useState('light');
  const [selectedLeague, setSelectedLeague] = useState(null);

  // Notification polling — starts when logged in, stops on logout
  useNotificationPolling({ accessToken, fetchWallet });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Clear bets on logout
  useEffect(() => {
    if (!user) setBets([]);
  }, [user]);

  const toggleTheme = () => setTheme((t) => (t === 'light' ? 'dark' : 'light'));

  const addBet = (bet) => {
    if (!user) { navigate('/login'); return; }
    setBets((prev) => {
      // Prevent same fixture twice
      const sameFixture = prev.find((b) => b.fixture_id === bet.fixture_id);
      if (sameFixture) return prev.map((b) => b.fixture_id === bet.fixture_id ? { ...bet, stake: b.stake } : b);
      // Toggle off if same odd clicked again
      const exists = prev.find((b) => b.id === bet.id);
      if (exists) return prev.filter((b) => b.id !== bet.id);
      return [...prev, { ...bet, stake: '' }];
    });
  };

  const removeBet   = (id)        => setBets((prev) => prev.filter((b) => b.id !== id));
  const updateStake = (id, stake) => setBets((prev) => prev.map((b) => (b.id === id ? { ...b, stake } : b)));

  const handleLogout = async () => { await logout(); };

  if (isLoading) return null;

  return (
    <Routes>
      {/* Standalone pages */}
      <Route path="/login"     element={<LoginPage />} />
      <Route path="/register"  element={<RegisterPage />} />
      <Route path="/compte"    element={<UserAccountPage />} />
      <Route path="/dashboard" element={<DashboardPage user={user} balance={balance} onLogout={handleLogout} />} />

      {/* Main app */}
      <Route path="*" element={
        <div className="app">
          <Navbar
            user={user}
            balance={balance}
            betCount={bets.length}
            onLogout={handleLogout}
            theme={theme}
            toggleTheme={toggleTheme}
          />
          <div className="app-body">
            <Sidebar
              selectedLeague={selectedLeague}
              onSelectLeague={(league) => {
                setSelectedLeague(league);
                navigate('/sports');
              }}
            />
            <main className="app-main">
              <Routes>
                <Route path="/"            element={<HomePage   addBet={addBet} bets={bets} />} />
                <Route path="/sports"      element={<SportsPage addBet={addBet} bets={bets} selectedLeague={selectedLeague} />} />
                <Route path="/live"        element={<LivePage   addBet={addBet} bets={bets} />} />
                <Route path="/promotions"    element={<PromotionsPage />} />
                <Route path="/championnat"   element={<ChampionshipPage />} />
                <Route path="/wallet"      element={<WalletPage />} />
                <Route path="/mes-paris"   element={<MyBetsPage />} />
              </Routes>
            </main>
            <BetSlip
              bets={bets}
              balance={balance}
              onRemove={removeBet}
              onUpdateStake={updateStake}
              onPlace={placeBet}
              onBetsCleared={() => setBets([])}
              onNeedAuth={() => navigate('/login')}
              onNeedDeposit={() => navigate('/wallet')}
            />
            <MobileBetBar
              bets={bets}
              balance={balance}
              onRemove={removeBet}
              onUpdateStake={updateStake}
              onPlace={placeBet}
              onBetsCleared={() => setBets([])}
              onNeedAuth={() => navigate('/login')}
              onNeedDeposit={() => navigate('/wallet')}
            />
          </div>
        </div>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <Router>
      <AppInner />
      <ToastContainer
        position="top-right"
        autoClose={6000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </Router>
  );
}
